/**
 * MODULE: EmailTemplateEditModal
 * PURPOSE: Edit golden-path email templates (subject, body, metadata) — Assistant / Sales Hub parity
 * DEPENDENCIES: flowbite-react, @domain/templates/frontend
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-06 — PATCH /api/v1/templates/:id content.subject + content.body
 */

import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  TextInput,
  Textarea,
  Select,
} from 'flowbite-react';
import { UserIcon } from '@heroicons/react/24/outline';
import {
  extractEmailTemplateSource,
  getEmailTemplate,
  updateEmailTemplate,
} from '@domain/templates/frontend';
import { useToast } from '@design-system/components';
import { CREATE_CATEGORY_OPTIONS } from '../../config/template-categories';
import InsertFieldDropdown from './InsertFieldDropdown';
import QuickInsertFields from './QuickInsertFields';
import { ContactFieldsService, type ContactField } from '../../services/contactFieldsService';

interface EmailTemplateEditModalProps {
  isOpen: boolean;
  templateId: string | null;
  onClose: () => void;
  onSuccess: (result?: { title?: string }) => void;
}

export default function EmailTemplateEditModal({
  isOpen,
  templateId,
  onClose,
  onSuccess,
}: EmailTemplateEditModalProps) {
  const categories = CREATE_CATEGORY_OPTIONS.email;
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: categories[0]?.value ?? 'outreach',
    subject: '',
    previewText: '',
    bodyContent: '',
    sharedWith: 'everyone' as 'everyone' | 'team' | 'private',
  });

  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (!isOpen || !templateId) return;
    let cancelled = false;
    setIsLoading(true);
    void getEmailTemplate(templateId)
      .then(({ template, subject, body }) => {
        if (cancelled) return;
        const meta = (template.metadata ?? {}) as Record<string, unknown>;
        const category =
          (typeof meta.hub_category === 'string' && meta.hub_category) ||
          (typeof meta.category === 'string' && meta.category) ||
          categories[0]?.value ||
          'outreach';
        const shared =
          meta.shared_with === 'team' || meta.shared_with === 'private'
            ? meta.shared_with
            : 'everyone';
        const preview =
          typeof (template.content as Record<string, unknown> | undefined)?.preview_text === 'string'
            ? ((template.content as Record<string, unknown>).preview_text as string)
            : '';
        setFormData({
          name: template.title || '',
          description: template.description || '',
          category,
          subject,
          previewText: preview,
          bodyContent: body,
          sharedWith: shared,
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          showToast({
            message: err instanceof Error ? err.message : 'Failed to load template',
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, templateId, categories, showToast]);

  const insertFieldIntoSubject = (field: ContactField) => {
    if (!subjectInputRef.current) return;
    const input = subjectInputRef.current;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const newValue =
      formData.subject.substring(0, start) + field.placeholder + formData.subject.substring(end);
    setFormData({ ...formData, subject: newValue });
  };

  const insertFieldIntoBody = (field: ContactField) => {
    if (!bodyTextareaRef.current) return;
    const textarea = bodyTextareaRef.current;
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const newValue =
      formData.bodyContent.substring(0, start) +
      field.placeholder +
      formData.bodyContent.substring(end);
    setFormData({ ...formData, bodyContent: newValue });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateId || !formData.name.trim()) {
      showToast({ message: 'Template name is required', variant: 'error' });
      return;
    }
    if (!formData.subject.trim() || !formData.bodyContent.trim()) {
      showToast({ message: 'Subject and email body are required', variant: 'error' });
      return;
    }
    try {
      setIsSaving(true);
      await updateEmailTemplate(templateId, {
        title: formData.name.trim(),
        description: formData.description.trim() || undefined,
        category: formData.category,
        subject: formData.subject,
        body: formData.bodyContent,
        preview_text: formData.previewText || undefined,
        shared_with: formData.sharedWith,
      });
      showToast({
        message: 'Email template updated — Assistant will use the latest version',
        variant: 'success',
      });
      onSuccess({ title: formData.name.trim() });
      onClose();
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : 'Failed to update template',
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!templateId) return null;

  return (
    <Modal show={isOpen} onClose={onClose} size="4xl">
      <ModalHeader>
        <div className="flex items-center gap-3">
          <span className="text-2xl">✉️</span>
          <span>Edit Email Template</span>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody
          className="max-h-[80vh] overflow-y-auto"
          data-testid="email-template-edit-modal"
        >
          {isLoading ? (
            <p className="py-8 text-center text-sm text-gray-500">Loading template…</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email-edit-name">Template Name *</Label>
                  <TextInput
                    id="email-edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Owner</Label>
                  <div className="mt-1 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">You</span>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="email-edit-subject">Subject Line *</Label>
                  <InsertFieldDropdown onFieldSelect={insertFieldIntoSubject} />
                </div>
                <TextInput
                  id="email-edit-subject"
                  ref={subjectInputRef}
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email-edit-shared">Shared with</Label>
                  <Select
                    id="email-edit-shared"
                    value={formData.sharedWith}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sharedWith: e.target.value as 'everyone' | 'team' | 'private',
                      })
                    }
                    className="mt-1"
                  >
                    <option value="everyone">Everyone</option>
                    <option value="team">My Team</option>
                    <option value="private">Private</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="email-edit-category">Category *</Label>
                  <Select
                    id="email-edit-category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="mt-1"
                    required
                  >
                    {categories.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div>
                <Label>Email Body *</Label>
                <QuickInsertFields onFieldSelect={insertFieldIntoBody} />
                <Textarea
                  id="email-edit-body"
                  ref={bodyTextareaRef}
                  value={formData.bodyContent}
                  onChange={(e) => setFormData({ ...formData, bodyContent: e.target.value })}
                  rows={10}
                  required
                />
              </div>

              <div>
                <Label htmlFor="email-edit-preview">Preview Text (optional)</Label>
                <TextInput
                  id="email-edit-preview"
                  value={formData.previewText}
                  onChange={(e) => setFormData({ ...formData, previewText: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email-edit-desc">Description (optional)</Label>
                <Textarea
                  id="email-edit-desc"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="gray" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button color="purple" type="submit" disabled={isLoading || isSaving}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
